import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_SURVEY_GIV_CHARTS } from 'graphql/queries';
import DateFilter from './DateFilter';
import { useContext } from 'react';
import ResultContext from './ResultContext';
import { endDate, startDate } from './constant';
import GivGraph from './GivGraph';
import { questionTypes } from 'constants/questionTypes';
import { graphqlQuestionTypeToQuestionTypeMap } from 'constants/global';
import { Card, CardBody, Col, Row } from 'reactstrap';
import AverageValues from './AverageValues';
import { removeHtmlTags } from 'helpers/global';
import PresetDataSetFilter from './PresetDataSetFilter';
import queryString from 'query-string';
import qs from 'qs';
import { useLocation, useHistory } from 'react-router';
import { GET_SURVEY_GIV_BENCHMARKING_CHARTS } from 'graphql/queries';
import BenchmarkingGraph from './BenchmarkingGraph';
import { cleanQueryString } from 'helpers/global';

const Content = () => {
  const { id } = useParams();

  const { filterDateFrom, setFilterDateFrom, filterDateUntil, setFilterDateUntil } =
    useContext(ResultContext);

  const location = useLocation();

  const {
    loading: isLoadingGivCharts,
    data: dataGivCharts,
    refetch: refetchDataGivCharts
  } = useQuery(GET_SURVEY_GIV_CHARTS, {
    variables: {
      first: 100,
      page: 1,
      id,
      responsesFrom: startDate,
      responsesUntil: endDate
    },
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: isLoadingBenchmarkingCharts,
    data: dataGivBenchmarkingCharts,
    refetch: refetchGivBenchmarkingCharts
  } = useQuery(GET_SURVEY_GIV_BENCHMARKING_CHARTS, {
    variables: {
      first: 100,
      page: 1,
      id,
      responsesFrom: startDate,
      responsesUntil: endDate
    },
    notifyOnNetworkStatusChange: true
  });

  const history = useHistory();

  const onDateFilterSubmit = (dateFrom, dateUntil, interval) => {
    if ((dateFrom = dateFrom.format('YYYY-MM-DD'))) setFilterDateFrom(dateFrom);
    if ((dateUntil = dateUntil.format('YYYY-MM-DD'))) setFilterDateUntil(dateUntil);

    const variables = {
      responsesFrom: dateFrom,
      responsesUntil: dateUntil,
      interval
    };

    refetchDataGivCharts(variables);

    refetchGivBenchmarkingCharts({
      responsesFrom: dateFrom,
      responsesUntil: dateUntil
    });

    // update params
    const params = new URL(document.location.href).searchParams;
    params.set('start_date', dateFrom);
    params.set('end_date', dateUntil);
    params.set('interval', interval);
    window.history.replaceState(null, null, '?' + params);
  };

  const onPresetDataSubmit = (presetDataId, presetDataSets) => {
    const qsParsed = qs.parse(cleanQueryString(location.search));

    const key = 'preset_data_set_id';

    if (presetDataSets.length <= 0) {
      delete qsParsed[key][presetDataId];
    } else {
      qsParsed[key] = qsParsed[key] || {};
      qsParsed[key][presetDataId] = [];

      for (let i = 0; i < presetDataSets.length; i++) {
        qsParsed[key][presetDataId].push(presetDataSets[i]);
      }
    }

    let presetDataSetIds = undefined;

    if (Object.keys(qsParsed[key]).length >= 1) {
      presetDataSetIds = Object.keys(qsParsed[key]).reduce((curr, acc) => {
        return qsParsed[key][acc].concat(curr);
      }, []);
    }

    refetchDataGivCharts({
      presetDataSetId: presetDataSetIds
    });

    refetchGivBenchmarkingCharts({
      presetDataSetId: presetDataSetIds
    });

    history.push(
      queryString.stringifyUrl({
        url: location.pathname,
        query: queryString.parse(qs.stringify(qsParsed, { encode: false }))
      })
    );
  };

  const onPresetDataReset = () => {
    const parsedQuery = queryString.parse(location.search);

    const qsParsed = qs.parse(queryString.stringify(parsedQuery));

    delete qsParsed.preset_data_set_id;

    refetchDataGivCharts({
      presetDataSetId: undefined
    });

    history.push(
      queryString.stringifyUrl({
        url: location.pathname,
        query: qsParsed
      })
    );
  };

  return (
    <>
      <div className="d-print-none">
        <DateFilter
          onSubmit={onDateFilterSubmit}
          defaultStartDate={startDate}
          defaultEndDate={endDate}
          className="d-print-none"
        />
      </div>

      <div className="d-print-none">
        <PresetDataSetFilter
          surveyId={id}
          onSubmit={onPresetDataSubmit}
          onReset={onPresetDataReset}
          className="d-print-none"
        />
      </div>

      <div className="result-survey-print">
        <div className="mb-card no-split">
          <GivGraph
            dateFrom={filterDateFrom}
            dateUntil={filterDateUntil}
            isLoading={isLoadingGivCharts}
            data={dataGivCharts?.getSurveyGivCharts[0]}
          />
        </div>

        <AverageValues
          dateFrom={filterDateFrom}
          dateUntil={filterDateUntil}
          isLoading={isLoadingGivCharts}
          data={dataGivCharts?.getSurveyGivCharts
            .slice(1)
            .map((elm) => ({ label: removeHtmlTags(elm.name), value: elm.avg }))}
        />

        <Row>
          {dataGivCharts?.getSurveyGivCharts.slice(1).map((elm, index) => {
            const label = questionTypes[graphqlQuestionTypeToQuestionTypeMap[elm.questionType]].label;
            return (
              <Col xs={12} sm={6} key={`question-${index}`} className="mb-card">
                <GivGraph
                  eyebrow={label}
                  key={index}
                  dateFrom={filterDateFrom}
                  dateUntil={filterDateUntil}
                  isLoading={isLoadingGivCharts}
                  data={elm}
                />
              </Col>
            );
          })}
        </Row>

        {isLoadingBenchmarkingCharts && <p>Loading...</p>}

        {dataGivBenchmarkingCharts?.getSurveyGivBenchmarkingCharts.length <= 0 ? (
          <Row>
            <Col className="mb-card">
              <Card>
                <CardBody>There is no data!</CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row>
            {dataGivBenchmarkingCharts?.getSurveyGivBenchmarkingCharts.map((elm, index) => {
              return (
                <Col xs={12} sm={12} key={`question-${index}`} className="mb-card no-split">
                  <BenchmarkingGraph
                    id={index}
                    eyebrow="Benchmarking"
                    key={index}
                    dateFrom={filterDateFrom}
                    dateUntil={filterDateUntil}
                    isLoading={isLoadingGivCharts}
                    data={elm}
                  />
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </>
  );
};

export default Content;
